import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { addDays, differenceInDays, parseISO, isAfter, isBefore, startOfDay, isToday, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getWorkEndHourForDate, WORK_HOURS } from '@/lib/disponibilite-utils';
import { SeuilsAlertes, DEFAULT_SEUILS } from '@/types/alertes';
import { BudgetVentile, CATEGORY_LABELS, BudgetCategory } from '@/types/budgetVentile.types';
import { ConflitAbsenceAffectation, getTypeAbsenceLabel } from '@/types/conflits-absence';
import { getDateFinEffective, AffectationRetard, SuspensionInfo as SuspensionInfoDateUtils } from '@/lib/dateUtils';
import { useAuth } from '@/hooks/useAuth';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateAlertes } from '@/lib/queryInvalidation';

// Types d'actions possibles sur une alerte
export interface AlerteAction {
  label: string;
  variant: 'primary' | 'secondary' | 'success' | 'danger';
  type: 'navigate' | 'modal' | 'function';
  destination?: string;
  params?: Record<string, any>;
}

export interface Alerte {
  id: string;
  type: 'critique' | 'attention' | 'info' | 'suggestion';
  categorie:
    | 'budget'
    | 'retard'
    | 'equipe'
    | 'rotation'
    | 'fin_chantier'
    | 'facture'
    | 'devis'
    | 'performance'
    | 'planning'
    | 'paiement'
    | 'analyse_ia'
    | 'suspension'
    | 'conflits_absence';
  titre: string;
  description: string;
  chantierId?: string;
  chantierNom?: string;
  membreId?: string;
  membreNom?: string;
  valeur?: number;
  seuil?: number;
  // Nouveau : actions disponibles
  actions?: AlerteAction[];
  // Nouveau : données contextuelles pour les actions
  donnees?: Record<string, any>;
}

interface UseAlertesProps {
  entrepriseId: string | null;
}

interface ChantierData {
  id: string;
  nom_chantier: string;
  reference_chantier: string;
  date_debut_prevue: string | null;
  date_fin_estimee: string | null;
  date_fin_reelle: string | null;
  etat_chantier: string;
  statut: string;
  budget_ht: number | null;
  budget_ventile: BudgetVentile | null;
  updated_at?: string; // Pour versionner les alertes
  cloture_avec_succes?: boolean | null; // Clôturé via le flow de clôture
  // Détails des jours/demi-journées travaillés (pour calcul heure de fin)
  jours_details?: JourDetail[] | null;
  chantier_metrics_realtime?: {
    metrics: {
      marge_finale_pct?: number;
      marge_finale_euros?: number;
      budget_consomme_pct?: number;
      cout_total_reel?: number;
      jours_restants_rentables?: number;
    };
  };
  // Devis liés
  devis?: Array<{
    id: string;
    montant_ht: number;
    statut: string;
    actif: boolean;
    created_at: string;
  }>;
  // Dernière analyse IA
  analyses_ia?: Array<{
    id: string;
    created_at: string;
  }>;
}

// Type pour jours_details JSONB
interface JourDetail {
  date: string;
  period: 'AM' | 'PM' | 'FULL';
  value: number;
}

interface AffectationData {
  id: string;
  date_debut: string;
  date_fin: string;
  membre_equipe_id: string | null;
  equipe_id: string | null;
  chantier_id: string;
  heures_par_jour: number;
  jours_details?: JourDetail[];
  chantiers: {
    nom_chantier: string;
  };
  membres_equipe?: {
    prenom: string;
    nom: string;
    specialite?: string;
  };
  equipes?: {
    nom: string;
  };
}

interface MembreData {
  id: string;
  prenom: string;
  nom: string;
  statut: string;
  specialite?: string;
  equipe_id?: string | null;
}

interface EquipeData {
  id: string;
  nom: string;
}

// Type pour les suspensions actives
interface SuspensionData {
  id: string;
  chantier_id: string;
  date_suspension: string;
  raison: string;
  raison_detail: string | null;
  duree_estimee: string | null;
  date_reprise_estimee: string | null;
  type_suspension: string;
  chantiers: {
    nom_chantier: string;
  };
}

// Type pour les factures clients (alertes paiement)
interface FactureClientData {
  id: string;
  numero_facture: string;
  chantier_id: string;
  montant_ttc: number;
  date_echeance: string;
  statut: string;
  chantiers: {
    nom_chantier: string;
    entreprise_id: string;
  };
}

// ═══════════════════════════════════════════════════════════════
// FONCTIONS HELPER POUR AGRÉGATION DES ALERTES
// ═══════════════════════════════════════════════════════════════

/**
 * Retourne le label d'agrégation pour une catégorie d'alerte
 */
const getCategorieLabel = (categorie: string): string => {
  const labels: Record<string, string> = {
    'budget': 'chantiers en dépassement budget',
    'performance': 'chantiers en difficulté',
    'retard': 'chantiers en retard',
    'equipe': 'alertes équipe',
    'facture': 'factures à traiter',
    'paiement': 'paiements en attente',
    'suspension': 'chantiers suspendus',
    'fin_chantier': 'chantiers en fin de période',
    'devis': 'devis en attente',
    'planning': 'alertes planning',
    'rotation': 'rotations à planifier',
    'analyse_ia': 'analyses IA recommandées',
    'conflits_absence': 'conflits absence',
  };
  return labels[categorie] || 'alertes';
};

/**
 * Agrège les alertes similaires quand il y en a 2 ou plus
 * Regroupe par type + catégorie pour éviter la surcharge d'alertes
 *
 * @param alertes - Liste des alertes à agréger
 * @returns Liste des alertes agrégées (1 alerte → garde individuellement, ≥2 → agrège)
 */
const aggregateAlertes = (alertes: Alerte[]): Alerte[] => {
  // Grouper par type + categorie
  const grouped: Record<string, Alerte[]> = {};

  alertes.forEach(alerte => {
    const key = `${alerte.type}-${alerte.categorie}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(alerte);
  });

  // Transformer les groupes
  return Object.values(grouped).flatMap(group => {
    // Si 1 seule alerte, garder individuellement
    if (group.length <= 1) return group;

    // Si 2 ou plus, agréger
    const premier = group[0];
    const chantiersNoms = group
      .map(a => a.chantierNom)
      .filter(Boolean)
      .slice(0, 5); // Max 5 noms affichés

    const reste = group.length - chantiersNoms.length;
    const nomsList = chantiersNoms.join(', ') + (reste > 0 ? ` +${reste} autre${reste > 1 ? 's' : ''}` : '');

    return [{
      ...premier,
      id: `aggregated-${premier.type}-${premier.categorie}-${Date.now()}`,
      titre: `${group.length} ${getCategorieLabel(premier.categorie)}`,
      description: nomsList,
      donnees: {
        ...premier.donnees,
        alertesGroupees: group,
        count: group.length
      },
      actions: [] // Pas d'action globale — les boutons sont dans chaque sous-alerte (donnees.alertesGroupees)
    }];
  });
};

/**
 * Génère une clé d'alerte versionnée pour permettre la réapparition si les données changent.
 * La version est basée sur le jour de dernière modification (updated_at arrondi au jour).
 *
 * @param baseId - ID de base de l'alerte (ex: "budget-depasse-chantierId")
 * @param updatedAt - Date de dernière modification (ISO string ou null)
 * @returns Clé versionnée (ex: "budget-depasse-chantierId-v19789")
 */
const getVersionedAlerteKey = (baseId: string, updatedAt: string | null | undefined): string => {
  if (!updatedAt) return baseId;
  // Version = nombre de jours depuis epoch (change 1x par jour max)
  const dayVersion = Math.floor(Date.parse(updatedAt) / 86400000);
  return `${baseId}-v${dayVersion}`;
};

/** Nombre maximum d'alertes par chantier */
const MAX_ALERTES_PAR_CHANTIER = 3;

/**
 * Priorité intra-chantier pour décider quelles alertes garder.
 * Score bas = plus prioritaire.
 */
const PRIORITE_INTRA_CHANTIER: Record<string, number> = {
  'deficit-critique': 0,
  'retard-critique': 1,
  'budget-depasse': 2,
  'conflit-absence': 3,
  'sans-equipe': 4,
  'fin-proche': 5,
  'marge-faible': 6,
  'budget-attention': 7,
  'budget-cat': 8,
  'suspension': 9,
  'reprise': 9,
  'facturation': 10,
  'paiement': 10,
  'facture': 10,
  'devis': 11,
  'analyse-ia': 12,
};

/**
 * Extrait le score de priorité intra-chantier d'une alerte.
 * Match le préfixe le plus long possible dans la map.
 */
const getIntraChantierScore = (alerteId: string): number => {
  let bestScore = 99;
  let bestLen = 0;
  for (const [prefix, score] of Object.entries(PRIORITE_INTRA_CHANTIER)) {
    if (alerteId.startsWith(prefix) && prefix.length > bestLen) {
      bestScore = score;
      bestLen = prefix.length;
    }
  }
  return bestScore;
};

/**
 * Limite le nombre d'alertes par chantier.
 * Les alertes globales (sans chantierId) ne sont pas limitées.
 * Pour chaque chantier, on garde les N plus prioritaires selon PRIORITE_INTRA_CHANTIER.
 */
const limitAlertesParChantier = (alertes: Alerte[], max: number = MAX_ALERTES_PAR_CHANTIER): Alerte[] => {
  const globales: Alerte[] = [];
  const parChantier: Record<string, Alerte[]> = {};

  alertes.forEach(alerte => {
    if (!alerte.chantierId) {
      globales.push(alerte);
    } else {
      if (!parChantier[alerte.chantierId]) parChantier[alerte.chantierId] = [];
      parChantier[alerte.chantierId].push(alerte);
    }
  });

  // Pour chaque chantier, trier par priorité intra-chantier et garder les N premières
  const chantiersLimites = Object.values(parChantier).flatMap(group => {
    if (group.length <= max) return group;
    group.sort((a, b) => getIntraChantierScore(a.id) - getIntraChantierScore(b.id));
    return group.slice(0, max);
  });

  // Recombiner et re-trier par priorité globale (critique > attention > info > suggestion)
  const prioriteGlobale: Record<string, number> = { critique: 0, attention: 1, info: 2, suggestion: 3 };
  const combined = [...globales, ...chantiersLimites];
  combined.sort((a, b) => (prioriteGlobale[a.type] ?? 99) - (prioriteGlobale[b.type] ?? 99));
  return combined;
};

/**
 * Hook pour récupérer les alertes et notifications du pilotage
 * Catégories d'alertes :
 * - CRITIQUE (rouge) : Action immédiate requise
 * - ATTENTION (orange) : À surveiller
 * - INFO (bleu) : Bonnes nouvelles / Rappels
 */
export function useAlertes({ entrepriseId }: UseAlertesProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Alertes lues depuis Supabase (persistées)
  const [alertesLuesDB, setAlertesLuesDB] = useState<Set<string>>(new Set());

  // Fonction pour rafraîchir les données manuellement
  const refetch = useCallback(() => {
    if (entrepriseId) {
      invalidateAlertes(queryClient, entrepriseId);
    }
  }, [queryClient, entrepriseId]);

  // ============================================================================
  // QUERY : Charger toutes les données nécessaires aux alertes
  // ============================================================================

  const { data: alerteData, isLoading: loading } = useQuery({
    queryKey: queryKeys.alertes.all(entrepriseId!),
    queryFn: async () => {
      try {
      // Utiliser format() pour éviter les décalages UTC
      const today = format(startOfDay(new Date()), 'yyyy-MM-dd');

      // Charger les alertes lues depuis Supabase (si user connecté)
      let alertesLuesFromDB: string[] = [];
      if (user?.id) {
        const { data: alertesLuesData, error: alertesLuesError } = await supabase
          .from('alertes_lues')
          .select('alerte_key')
          .eq('user_id', user.id);

        if (!alertesLuesError && alertesLuesData) {
          alertesLuesFromDB = alertesLuesData.map(a => a.alerte_key);
        }
      }

        // Charger les chantiers actifs avec métriques, devis et analyses IA
        const { data: chantiersData, error: chantiersError } = await supabase
          .from('chantiers')
          .select(`
            id,
            nom_chantier,
            reference_chantier,
            date_debut_prevue,
            date_fin_estimee,
            date_fin_reelle,
            etat_chantier,
            statut,
            budget_ht,
            budget_ventile,
            jours_details,
            updated_at,
            chantier_metrics_realtime (metrics),
            devis (id, montant_ht, statut, actif, created_at),
            analyses_ia (id, created_at)
          `)
          .eq('entreprise_id', entrepriseId)
          .eq('statut', 'actif')
          .in('etat_chantier', ['en_cours', 'planifie']);

        if (chantiersError) {
          console.error('[useAlertes] Erreur chargement chantiers:', chantiersError);
        }

        // Charger les chantiers terminés (pour facturation et paiement)
        const { data: chantiersTerminesData, error: chantiersTerminesError } = await supabase
          .from('chantiers')
          .select(`
            id,
            nom_chantier,
            reference_chantier,
            date_fin_estimee,
            date_fin_reelle,
            etat_chantier,
            statut,
            cloture_avec_succes,
            chantier_metrics_realtime (metrics),
            devis (id, montant_ht, statut, actif, created_at)
          `)
          .eq('entreprise_id', entrepriseId)
          .eq('etat_chantier', 'termine')
          .eq('statut', 'actif');

        if (chantiersTerminesError) {
          console.error('[useAlertes] Erreur chargement chantiers terminés:', chantiersTerminesError);
        }

        // Charger les affectations (passées, présentes et futures) avec jours_details
        // Note: affectations_chantiers n'a pas de colonne entreprise_id, on filtre via la jointure chantiers
        const { data: affectationsData } = await supabase
          .from('affectations_chantiers')
          .select(`
            id,
            date_debut,
            date_fin,
            membre_equipe_id,
            equipe_id,
            chantier_id,
            heures_par_jour,
            jours_details,
            chantiers!inner (nom_chantier, entreprise_id),
            membres_equipe (prenom, nom, specialite),
            equipes (nom)
          `)
          .eq('chantiers.entreprise_id', entrepriseId)
          .gte('date_fin', today);

        // Charger les membres avec equipe_id pour lier membre -> équipe
        const { data: membresData } = await supabase
          .from('membres_equipe')
          .select('id, prenom, nom, statut, specialite, equipe_id')
          .eq('entreprise_id', entrepriseId)
          .in('statut', ['disponible', 'sur_chantier']);

        // Charger les équipes
        const { data: equipesData } = await supabase
          .from('equipes')
          .select('id, nom')
          .eq('entreprise_id', entrepriseId);

        // Charger les suspensions actives (date_reprise IS NULL)
        const { data: suspensionsData } = await supabase
          .from('suspensions_chantiers')
          .select(`
            id,
            chantier_id,
            date_suspension,
            raison,
            raison_detail,
            duree_estimee,
            date_reprise_estimee,
            type_suspension,
            chantiers!inner (nom_chantier, entreprise_id)
          `)
          .eq('chantiers.entreprise_id', entrepriseId)
          .is('date_reprise', null);

        // Charger les retards déjà enregistrés (via frais_chantier)
        // Évite la notification "Chantier en retard" si le retard a été ajouté manuellement
        const { data: retardsData } = await supabase
          .from('frais_chantier')
          .select('chantier_id')
          .eq('entreprise_id', entrepriseId)
          .or('type_frais.ilike.%Pénalité de retard%,type_frais.ilike.%Coût retard%');

        // Charger les affectations de RETARD (notes LIKE 'Retard chantier%')
        // Utilisé pour calculer date_fin_effective via getDateFinEffective()
        const { data: affectationsRetardData } = await supabase
          .from('affectations_chantiers')
          .select('chantier_id, date_fin, jours_travailles, jours_details, chantiers!inner(entreprise_id)')
          .eq('chantiers.entreprise_id', entrepriseId)
          .ilike('notes', 'Retard chantier%');

        // Charger les suspensions TERMINÉES (date_reprise IS NOT NULL)
        // Utilisé pour calculer date_fin_effective
        const { data: suspensionsTermineesData } = await supabase
          .from('suspensions_chantiers')
          .select('chantier_id, date_suspension, date_reprise, duree_suspension_jours, chantiers!inner(entreprise_id)')
          .eq('chantiers.entreprise_id', entrepriseId)
          .not('date_reprise', 'is', null);

        // Calculer date_fin_effective par chantier via getDateFinEffective()
        const datesFinMap = new Map<string, string>();
        (chantiersData || []).forEach((chantier: any) => {
          // Affectations de retard pour ce chantier
          const affRetard = (affectationsRetardData || [])
            .filter((a: any) => a.chantier_id === chantier.id)
            .map((a: any) => ({
              date_fin: a.date_fin,
              jours_travailles: a.jours_travailles || 0,
              jours_details: a.jours_details,
            }));

          // Suspensions terminées pour ce chantier
          const suspTerminees = (suspensionsTermineesData || [])
            .filter((s: any) => s.chantier_id === chantier.id)
            .map((s: any) => ({
              date_suspension: s.date_suspension,
              date_reprise: s.date_reprise,
              duree_suspension_jours: s.duree_suspension_jours || 0,
            }));

          // Calculer date_fin_effective
          const dateEffective = getDateFinEffective({
            date_fin_estimee: chantier.date_fin_estimee,
            affectationsRetard: affRetard,
            suspensionsTerminees: suspTerminees,
          });

          if (dateEffective) {
            datesFinMap.set(chantier.id, format(dateEffective, 'yyyy-MM-dd'));
          }
        });

        // Charger les seuils d'alertes personnalisés de l'entreprise
        const { data: seuilsData } = await supabase.rpc('get_seuils_alertes', {
          p_entreprise_id: entrepriseId,
        });
        // seuils traité dans le return ci-dessous

        // Charger les factures clients non payées pour alertes de paiement
        const { data: facturesData } = await supabase
          .from('factures_clients')
          .select(`
            id,
            numero_facture,
            chantier_id,
            montant_ttc,
            date_echeance,
            statut,
            chantiers!inner (nom_chantier, entreprise_id)
          `)
          .eq('chantiers.entreprise_id', entrepriseId)
          .in('statut', ['emise', 'partiellement_payee', 'en_retard'])
          .not('date_echeance', 'is', null);

        if (import.meta.env.DEV) {
          console.log('[useAlertes] Chantiers actifs chargés:', chantiersData?.length);
          console.log('[useAlertes] Date actuelle:', format(startOfDay(new Date()), 'yyyy-MM-dd'));
        }

        // Charger les comparaisons de budget pour les chantiers avec ventilation
        const chantiersAvecVentilation = (chantiersData || []).filter(
          (c: any) => c.budget_ventile !== null
        );
        const comparisonsMap = new Map<string, any>();
        for (const chantier of chantiersAvecVentilation) {
          const { data: comparison } = await supabase.rpc('get_budget_vs_reel', {
            p_chantier_id: chantier.id,
          });
          if (comparison) {
            comparisonsMap.set(chantier.id, comparison);
          }
        }

        // Charger les conflits absence/affectation
        const { data: conflitsData, error: conflitsError } = await supabase.rpc(
          'get_all_conflits_absence_affectation',
          { p_entreprise_id: entrepriseId }
        );
        if (conflitsError) {
          console.error('[useAlertes] Erreur chargement conflits:', conflitsError);
        }

        // Charger les totaux paiements pour les chantiers terminés récents (post-clôture)
        const today30 = startOfDay(new Date());
        const paiementTotalsTermines: Record<string, { reste_a_encaisser: number; total_a_encaisser: number; total_encaisse: number }> = {};
        const contratsSTTermines: Record<string, { totalResteAFacturer: number; nbContrats: number }> = {};

        const chantiersTerminesRecents = (chantiersTerminesData || []).filter((c: any) =>
          c.date_fin_reelle && c.cloture_avec_succes &&
          differenceInDays(today30, parseISO(c.date_fin_reelle)) >= 0 &&
          differenceInDays(today30, parseISO(c.date_fin_reelle)) <= 30
        );

        // Paiements client par chantier terminé récent
        for (const c of chantiersTerminesRecents) {
          try {
            const { data: totalsData } = await supabase.rpc('get_paiement_totals', { p_chantier_id: c.id });
            if (totalsData?.[0] && totalsData[0].reste_a_encaisser > 0) {
              paiementTotalsTermines[c.id] = totalsData[0];
            }
          } catch (e) {
            console.warn(`[useAlertes] Erreur paiement totals pour ${c.id}:`, e);
          }
        }

        // Contrats ST batch pour les chantiers terminés récents
        if (chantiersTerminesRecents.length > 0) {
          const terminesIds = chantiersTerminesRecents.map((c: any) => c.id);
          const { data: contratsData } = await supabase
            .from('contrats_sous_traitance')
            .select('id, chantier_id, montant_contrat_ht')
            .in('chantier_id', terminesIds);

          if (contratsData && contratsData.length > 0) {
            const contratIds = contratsData.map((c: any) => c.id);
            const { data: fraisData } = await supabase
              .from('frais_chantier')
              .select('contrat_st_id, montant_ht')
              .in('contrat_st_id', contratIds);

            // Agréger les frais par contrat
            const fraisParContrat: Record<string, number> = {};
            (fraisData || []).forEach((f: any) => {
              fraisParContrat[f.contrat_st_id] = (fraisParContrat[f.contrat_st_id] || 0) + (f.montant_ht || 0);
            });

            // Agréger par chantier
            const parChantier: Record<string, { reste: number; count: number }> = {};
            contratsData.forEach((c: any) => {
              const montant = c.montant_contrat_ht || 0;
              const facture = fraisParContrat[c.id] || 0;
              const reste = Math.max(montant - facture, 0);
              if (!parChantier[c.chantier_id]) {
                parChantier[c.chantier_id] = { reste: 0, count: 0 };
              }
              if (reste > 0) {
                parChantier[c.chantier_id].reste += reste;
                parChantier[c.chantier_id].count += 1;
              }
            });

            Object.entries(parChantier).forEach(([chantierId, data]) => {
              contratsSTTermines[chantierId] = { totalResteAFacturer: data.reste, nbContrats: data.count };
            });
          }
        }

        return {
          chantiers: (chantiersData || []) as unknown as ChantierData[],
          chantiersTermines: (chantiersTerminesData || []) as unknown as ChantierData[],
          affectations: (affectationsData || []) as unknown as AffectationData[],
          membres: (membresData || []) as MembreData[],
          equipes: (equipesData || []) as EquipeData[],
          suspensionsActives: (suspensionsData || []) as unknown as SuspensionData[],
          facturesClients: (facturesData || []) as unknown as FactureClientData[],
          chantiersAvecRetardEnregistre: new Set<string>(
            retardsData?.map(r => r.chantier_id).filter(Boolean) as string[] || []
          ),
          datesFinEffectives: datesFinMap,
          budgetComparisons: comparisonsMap,
          conflitsAbsence: (conflitsData || []) as ConflitAbsenceAffectation[],
          seuils: seuilsData ? { ...DEFAULT_SEUILS, ...seuilsData } : DEFAULT_SEUILS,
          alertesLuesFromDB,
          paiementTotalsTermines,
          contratsSTTermines,
        };
      } catch (error) {
        console.error('[useAlertes] Erreur chargement:', error);
        throw error;
      }
    },
    enabled: !!entrepriseId,
    staleTime: 2 * 60 * 1000, // 2 min
  });

  // Dériver les données depuis le résultat de la query
  const chantiers = alerteData?.chantiers ?? [];
  const chantiersTermines = alerteData?.chantiersTermines ?? [];
  const affectations = alerteData?.affectations ?? [];
  const membres = alerteData?.membres ?? [];
  const equipes = alerteData?.equipes ?? [];
  const suspensionsActives = alerteData?.suspensionsActives ?? [];
  const facturesClients = alerteData?.facturesClients ?? [];
  const chantiersAvecRetardEnregistre = alerteData?.chantiersAvecRetardEnregistre ?? new Set<string>();
  const datesFinEffectives = alerteData?.datesFinEffectives ?? new Map<string, string>();
  const budgetComparisons = alerteData?.budgetComparisons ?? new Map<string, any>();
  const conflitsAbsence = alerteData?.conflitsAbsence ?? [];
  const seuils = alerteData?.seuils ?? DEFAULT_SEUILS;
  const paiementTotalsTermines = alerteData?.paiementTotalsTermines ?? {};
  const contratsSTTermines = alerteData?.contratsSTTermines ?? {};

  // Mettre à jour les alertes lues depuis la query (effet secondaire)
  useEffect(() => {
    if (alerteData?.alertesLuesFromDB) {
      setAlertesLuesDB(new Set(alerteData.alertesLuesFromDB));
    }
  }, [alerteData?.alertesLuesFromDB]);

  // Générer les alertes à partir des données
  const alertes = useMemo(() => {
    const result: Alerte[] = [];
    const today = startOfDay(new Date());
    // Utiliser format() au lieu de toISOString() pour éviter les décalages UTC
    const todayStr = format(today, 'yyyy-MM-dd');

    // Set des chantiers avec suspension active (pour filtrer les alertes temporelles)
    // Un chantier suspendu ne doit pas déclencher d'alerte "retard" ou "fin proche"
    const chantiersAvecSuspensionActive = new Set(
      suspensionsActives.map(s => s.chantier_id)
    );

    // ══════════════════════════════════════════════════════════════════
    // 🔴 ALERTES CRITIQUES (Urgentes — Action requise)
    // ══════════════════════════════════════════════════════════════════

    for (const chantier of chantiers) {
      const metrics = chantier.chantier_metrics_realtime?.metrics;

      // 1. ALERTES PERFORMANCE (marge + jours rentables fusionnées)
      // Logique simplifiée :
      // - CAS 1a : Déficit avéré (marge < 0%) → alerte critique fusionnée avec jours de perte
      // - CAS 1b : Déficit imminent (marge >= 0 mais jours_restants <= seuil critique) → alerte critique
      // - CAS 2 : Marge faible mais positive → alerte attention (enrichie avec jours restants si dispo)

      if (metrics && metrics.marge_finale_pct !== undefined) {
        const margePct = metrics.marge_finale_pct;
        const joursRestants = metrics.jours_restants_rentables ?? null;
        const euros = metrics.marge_finale_euros || 0;

        // CAS 1a : Déficit avéré (marge < seuil critique, généralement 0%)
        if (margePct < seuils.marge_critique_pct) {
          const joursPerteText = joursRestants !== null && joursRestants < 0
            ? ` (${Math.abs(Math.floor(joursRestants))} jour${Math.abs(joursRestants) > 1 ? 's' : ''} de perte)`
            : '';

          result.push({
            id: `deficit-critique-${chantier.id}`,
            type: 'critique',
            categorie: 'performance',
            titre: 'Déficit critique',
            description: `${chantier.nom_chantier} : Marge ${margePct.toFixed(1)}%${joursPerteText} (${euros.toLocaleString('fr-FR')}€)`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            valeur: margePct,
            seuil: seuils.marge_critique_pct,
            donnees: {
              marge_pct: margePct,
              marge_euros: euros,
              jours_restants: joursRestants,
              budget_ht: chantier.budget_ht,
              cout_total: metrics.cout_total_reel,
            },
            actions: [
              { label: 'Voir le chantier', variant: 'secondary', type: 'navigate', destination: `/financial?tab=rentabilite&chantier=${chantier.id}` },
              { label: 'Analyser', variant: 'primary', type: 'navigate', destination: `/projects/${chantier.id}?tab=ai-analysis&action=launch` },
            ],
          });
        }
        // CAS 1b : Déficit imminent (marge >= 0 mais jours restants <= seuil critique)
        else if (joursRestants !== null && joursRestants > 0 && joursRestants <= seuils.jours_rentables_critique) {
          const joursRestantsInt = Math.floor(joursRestants);
          result.push({
            id: `deficit-critique-${chantier.id}`,
            type: 'critique',
            categorie: 'performance',
            titre: joursRestantsInt === 0 ? 'Déficit imminent' : `Déficit dans ${joursRestantsInt} jour${joursRestantsInt > 1 ? 's' : ''}`,
            description: `${chantier.nom_chantier} atteindra le déficit dans ${joursRestantsInt} jour${joursRestantsInt > 1 ? 's' : ''} (marge actuelle : ${margePct.toFixed(1)}%)`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            valeur: joursRestants,
            seuil: seuils.jours_rentables_critique,
            donnees: {
              jours_restants: joursRestants,
              marge_pct: margePct,
              marge_euros: euros,
            },
            actions: [
              { label: 'Voir rentabilité', variant: 'primary', type: 'navigate', destination: `/financial?tab=rentabilite&chantier=${chantier.id}` },
              { label: 'Analyser', variant: 'secondary', type: 'navigate', destination: `/projects/${chantier.id}?tab=ai-analysis&action=launch` },
            ],
          });
        }
        // CAS 2 : Marge faible mais positive (enrichie avec jours restants si disponibles)
        else if (margePct >= seuils.marge_critique_pct && margePct < seuils.marge_faible_pct) {
          const joursRestantsText = joursRestants !== null && joursRestants > 0
            ? ` — encore ${Math.floor(joursRestants)} jour${Math.floor(joursRestants) > 1 ? 's' : ''} rentable${Math.floor(joursRestants) > 1 ? 's' : ''}`
            : '';

          result.push({
            id: `marge-faible-${chantier.id}`,
            type: 'attention',
            categorie: 'performance',
            titre: 'Marge faible',
            description: `${chantier.nom_chantier} : marge à ${margePct.toFixed(1)}% (seuil min : ${seuils.marge_faible_pct}%)${joursRestantsText}`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            valeur: margePct,
            seuil: seuils.marge_faible_pct,
            donnees: {
              marge_pct: margePct,
              marge_euros: euros,
              seuil_min: seuils.marge_faible_pct,
              jours_restants: joursRestants,
            },
            actions: [
              { label: 'Voir coûts', variant: 'primary', type: 'navigate', destination: `/financial?tab=expenses&chantier=${chantier.id}` },
            ],
          });
        }
      }

      // 2. Retard critique (chantier en retard sur date fin prévue)
      // Déclenché dès que la date de fin est dépassée (>= 1 jour de retard)
      // MAIS ignoré si :
      // - l'utilisateur a déjà enregistré le retard via le simulateur de pénalités
      // - le chantier a une suspension active (pas de sens d'alerter sur un retard si suspendu)
      if (chantier.date_fin_estimee && chantier.etat_chantier === 'en_cours') {
        const hasSuspensionActive = chantiersAvecSuspensionActive.has(chantier.id);
        const dateFin = parseISO(chantier.date_fin_estimee);
        const joursRetard = differenceInDays(today, dateFin);
        const retardDejaEnregistre = chantiersAvecRetardEnregistre.has(chantier.id);
        if (import.meta.env.DEV) console.log('[useAlertes] Vérification retard:', chantier.nom_chantier, { joursRetard, retardDejaEnregistre, hasSuspensionActive });
        // Alerte dès 1 jour de retard (date de fin dépassée)
        // Sauf si le retard a déjà été enregistré manuellement ou si suspension active
        if (joursRetard >= 1 && !retardDejaEnregistre && !hasSuspensionActive) {
          result.push({
            id: `retard-critique-${chantier.id}`,
            type: 'critique',
            categorie: 'retard',
            titre: 'Chantier en retard',
            description: `"${chantier.nom_chantier}" a dépassé sa date de fin de ${joursRetard} jour${joursRetard > 1 ? 's' : ''} (fin prévue : ${format(dateFin, 'dd/MM/yyyy')})`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            valeur: joursRetard,
            donnees: {
              jours_retard: joursRetard,
              date_fin_prevue: chantier.date_fin_estimee,
              date_debut: chantier.date_debut_prevue,
            },
            actions: [
              {
                label: 'Voir le chantier',
                variant: 'secondary',
                type: 'navigate',
                destination: `/projects/${chantier.id}`,
              },
              {
                label: 'Terminer',
                variant: 'success',
                type: 'navigate',
                destination: `/projects/${chantier.id}?terminer=true`,
              },
              {
                label: 'Retard',
                variant: 'danger',
                type: 'navigate',
                destination: `/financial?tab=penalty-simulator&chantier=${chantier.id}`,
              },
            ],
          });
        }
      }

      // 3. Alertes Budget avec déduplication intelligente
      // ═══════════════════════════════════════════════════════════════════════
      // Règles de déduplication :
      // - Global critique → UNE seule alerte avec détail des catégories
      // - Global attention + catégorie critique → DEUX alertes (info différente)
      // - Global OK → alertes catégories seules
      // ═══════════════════════════════════════════════════════════════════════

      const budgetGlobalPct = metrics?.budget_consomme_pct ?? 0;
      const budgetGlobalCritique = budgetGlobalPct > seuils.budget_critique_pct;
      const budgetGlobalAttention = budgetGlobalPct >= seuils.budget_attention_pct && !budgetGlobalCritique;

      // D'abord, collecter les catégories problématiques
      type CategorieProblematique = {
        key: BudgetCategory;
        pct: number;
        ecart: number;
        type: 'critique' | 'attention';
        label: string;
      };
      const categoriesProblematiques: CategorieProblematique[] = [];

      if (chantier.budget_ventile) {
        const budgetComparison = budgetComparisons.get(chantier.id);
        if (budgetComparison?.has_ventilation && budgetComparison?.categories) {
          const categories = budgetComparison.categories;
          const categoriesKeys: BudgetCategory[] = ['main_oeuvre', 'materiaux', 'sous_traitance', 'frais_annexes'];

          for (const catKey of categoriesKeys) {
            const catData = categories[catKey];
            if (!catData || catData.prevu === 0) continue;
            const pct = catData.pourcentage;

            if (pct >= seuils.budget_categorie_critique_pct) {
              categoriesProblematiques.push({
                key: catKey,
                pct,
                ecart: catData.ecart,
                type: 'critique',
                label: CATEGORY_LABELS[catKey],
              });
            } else if (pct >= seuils.budget_categorie_attention_pct) {
              categoriesProblematiques.push({
                key: catKey,
                pct,
                ecart: catData.ecart,
                type: 'attention',
                label: CATEGORY_LABELS[catKey],
              });
            }
          }
        }
      }

      const categoriesCritiques = categoriesProblematiques.filter(c => c.type === 'critique');

      // Cas 1 : Budget global critique → UNE seule alerte avec détail catégories
      if (budgetGlobalCritique && metrics) {
        const depassement = metrics.budget_consomme_pct! - 100;
        // Détail des catégories critiques
        const detailCategories = categoriesCritiques.length > 0
          ? ` — ${categoriesCritiques.map(c => `${c.label} +${(c.pct - 100).toFixed(0)}%`).join(', ')}`
          : '';

        result.push({
          id: getVersionedAlerteKey(`budget-depasse-${chantier.id}`, chantier.updated_at),
          type: 'critique',
          categorie: 'budget',
          titre: 'Budget dépassé',
          description: `${chantier.nom_chantier} : dépenses dépassent le budget de +${depassement.toFixed(0)}%${detailCategories}`,
          chantierId: chantier.id,
          chantierNom: chantier.nom_chantier,
          valeur: metrics.budget_consomme_pct,
          seuil: seuils.budget_critique_pct,
          donnees: {
            budget_consomme_pct: metrics.budget_consomme_pct,
            depassement_pct: depassement,
            budget_ht: chantier.budget_ht,
            cout_total: metrics.cout_total_reel,
            categories_critiques: categoriesCritiques.map(c => c.key),
          },
          actions: [
            { label: 'Voir les dépenses', variant: 'secondary', type: 'navigate', destination: `/financial?tab=factures&chantier=${chantier.id}` },
          ],
        });
      }
      // Cas 2 : Budget global attention + au moins une catégorie critique → DEUX alertes
      else if (budgetGlobalAttention && metrics) {
        // Alerte attention globale
        result.push({
          id: getVersionedAlerteKey(`budget-attention-${chantier.id}`, chantier.updated_at),
          type: 'attention',
          categorie: 'budget',
          titre: 'Budget bientôt épuisé',
          description: `${chantier.nom_chantier} : budget consommé à ${budgetGlobalPct.toFixed(0)}%`,
          chantierId: chantier.id,
          chantierNom: chantier.nom_chantier,
          valeur: budgetGlobalPct,
          seuil: seuils.budget_attention_pct,
          donnees: {
            budget_consomme_pct: budgetGlobalPct,
            budget_ht: chantier.budget_ht,
          },
          actions: [
            { label: 'Voir les dépenses', variant: 'secondary', type: 'navigate', destination: `/financial?tab=factures&chantier=${chantier.id}` },
          ],
        });

        // + Alertes catégories critiques (info différente : le global n'est pas encore critique)
        for (const cat of categoriesCritiques) {
          result.push({
            id: getVersionedAlerteKey(`budget-cat-depasse-${chantier.id}-${cat.key}`, chantier.updated_at),
            type: 'critique',
            categorie: 'budget',
            titre: `Budget ${cat.label} dépassé`,
            description: `${chantier.nom_chantier} : ${cat.label} à ${cat.pct.toFixed(0)}% (+${Math.abs(cat.ecart).toLocaleString('fr-FR')} €)`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            valeur: cat.pct,
            seuil: seuils.budget_categorie_critique_pct,
            donnees: {
              categorie: cat.key,
              ecart: cat.ecart,
            },
            actions: [
              { label: 'Voir budget', variant: 'primary', type: 'navigate', destination: `/projects/${chantier.id}?scrollTo=budget-details` },
            ],
          });
        }
      }
      // Cas 3 : Budget global OK → alertes catégories seules
      else if (categoriesProblematiques.length > 0) {
        for (const cat of categoriesProblematiques) {
          result.push({
            id: getVersionedAlerteKey(`budget-cat-${cat.type === 'critique' ? 'depasse' : 'attention'}-${chantier.id}-${cat.key}`, chantier.updated_at),
            type: cat.type,
            categorie: 'budget',
            titre: cat.type === 'critique'
              ? `Budget ${cat.label} dépassé`
              : `Budget ${cat.label} bientôt épuisé`,
            description: cat.type === 'critique'
              ? `${chantier.nom_chantier} : ${cat.label} à ${cat.pct.toFixed(0)}% (+${Math.abs(cat.ecart).toLocaleString('fr-FR')} €)`
              : `${chantier.nom_chantier} : ${cat.label} à ${cat.pct.toFixed(0)}% (reste ${cat.ecart.toLocaleString('fr-FR')} €)`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            valeur: cat.pct,
            seuil: cat.type === 'critique' ? seuils.budget_categorie_critique_pct : seuils.budget_categorie_attention_pct,
            donnees: {
              categorie: cat.key,
              ecart: cat.ecart,
            },
            actions: [
              { label: 'Voir budget', variant: cat.type === 'critique' ? 'primary' : 'secondary', type: 'navigate', destination: `/projects/${chantier.id}?scrollTo=budget-details` },
            ],
          });
        }
      }

      // 4. Chantier sans équipe - seuil personnalisable
      if (chantier.etat_chantier === 'planifie' && chantier.date_debut_prevue) {
        const dateDebut = parseISO(chantier.date_debut_prevue);
        const joursAvantDebut = differenceInDays(dateDebut, today);

        if (joursAvantDebut >= 0 && joursAvantDebut <= seuils.chantier_sans_equipe_jours) {
          // Vérifier s'il y a des affectations pour ce chantier
          const affectationsChantier = affectations.filter(
            (a) => a.chantier_id === chantier.id && a.date_debut <= chantier.date_debut_prevue!
          );

          if (affectationsChantier.length === 0) {
            result.push({
              id: `sans-equipe-${chantier.id}`,
              type: 'critique',
              categorie: 'equipe',
              titre: 'Chantier sans équipe',
              description: `${chantier.nom_chantier} démarre dans ${joursAvantDebut} jour${joursAvantDebut > 1 ? 's' : ''} mais aucune équipe affectée`,
              chantierId: chantier.id,
              chantierNom: chantier.nom_chantier,
              valeur: joursAvantDebut,
              donnees: {
                jours_avant_debut: joursAvantDebut,
                date_debut: chantier.date_debut_prevue,
              },
              actions: [
                { label: 'Affecter une équipe', variant: 'primary', type: 'navigate', destination: `/projects/${chantier.id}?tab=team` },
              ],
            });
          }
        }
      }
    }

    // ══════════════════════════════════════════════════════════════════
    // 🟠 ATTENTION (À surveiller)
    // ══════════════════════════════════════════════════════════════════

    // Helper : vérifie si une affectation est active pour un jour donné (utilise jours_details)
    const isAffectationActiveOnDay = (aff: AffectationData, dateStr: string): boolean => {
      // Vérifier d'abord si le jour est dans la plage date_debut - date_fin
      const debutStr = aff.date_debut.split('T')[0];
      const finStr = aff.date_fin.split('T')[0];
      if (dateStr < debutStr || dateStr > finStr) {
        return false;
      }
      // Si jours_details est disponible, utiliser cette info précise
      if (aff.jours_details && Array.isArray(aff.jours_details) && aff.jours_details.length > 0) {
        return aff.jours_details.some(jd => jd.date === dateStr);
      }
      // Fallback: considérer actif si dans la plage
      return true;
    };

    // 1. Membres sans affectation (prendre en compte affectations individuelles ET par équipe)
    // Collecter les membre_equipe_id avec affectation directe aujourd'hui
    const membresAvecAffectationDirecte = new Set<string>();
    // Collecter les equipe_id avec affectation aujourd'hui
    const equipesAvecAffectationAujourdhui = new Set<string>();

    affectations.forEach((aff) => {
      if (isAffectationActiveOnDay(aff, todayStr)) {
        // Affectation individuelle
        if (aff.membre_equipe_id) {
          membresAvecAffectationDirecte.add(aff.membre_equipe_id);
        }
        // Affectation par équipe
        if (aff.equipe_id) {
          equipesAvecAffectationAujourdhui.add(aff.equipe_id);
        }
      }
    });

    // Membres sans affectation = membres disponibles qui n'ont ni affectation directe ni leur équipe affectée
    const membresSansAffectation = membres.filter((m) => {
      if (m.statut !== 'disponible') return false;
      // A une affectation directe ?
      if (membresAvecAffectationDirecte.has(m.id)) return false;
      // Son équipe est affectée ?
      if (m.equipe_id && equipesAvecAffectationAujourdhui.has(m.equipe_id)) return false;
      // Pas d'affectation
      return true;
    });

    if (membresSansAffectation.length > 0) {
      // Liste complète des noms
      const nomsComplets = membresSansAffectation.map((m) => `${m.prenom} ${m.nom}`);
      // Liste courte pour l'affichage replié (max 5)
      const nomsCourts = nomsComplets.slice(0, 5);
      const autresCount = membresSansAffectation.length - nomsCourts.length;
      const nomsStr = nomsCourts.join(', ') + (autresCount > 0 ? ` +${autresCount} autre${autresCount > 1 ? 's' : ''}` : '');

      result.push({
        id: 'membres-sans-affectation',
        type: 'attention',
        categorie: 'equipe',
        titre: 'Membres sans affectation',
        description: nomsStr,
        valeur: membresSansAffectation.length,
        // Stocker la liste complète pour l'affichage déplié
        donnees: {
          membres: membresSansAffectation.map((m) => ({
            id: m.id,
            nom: `${m.prenom} ${m.nom}`,
            specialite: m.specialite,
          })),
          nomsComplets: nomsComplets.join(', '),
        },
        actions: [
          {
            label: 'Voir le planning',
            variant: 'primary',
            type: 'function',
            params: { action: 'open_planning' },
          },
        ],
      });
    }

    // 3. Chantier bientôt terminé / terminé aujourd'hui - seuil personnalisable
    // Utilise la date de fin EFFECTIVE (avec retards et suspensions)
    // Couvre à la fois J-7 à J-1 (bientôt terminé) et J-0 (terminé aujourd'hui)
    chantiers.forEach((chantier) => {
      if (chantier.etat_chantier !== 'en_cours') return;
      if (chantiersAvecSuspensionActive.has(chantier.id)) return;

      const dateFinEffectiveStr = datesFinEffectives.get(chantier.id) || chantier.date_fin_estimee;
      if (!dateFinEffectiveStr) return;

      const dateFin = parseISO(dateFinEffectiveStr);
      const joursRestants = differenceInDays(dateFin, today);
      const termineAujourdhui = isToday(dateFin);

      // CAS A : Jour J — Chantier se termine aujourd'hui
      if (termineAujourdhui) {
        const workEndHour = getWorkEndHourForDate(
          chantier.jours_details as JourDetail[] | null,
          format(new Date(), 'yyyy-MM-dd')
        );
        const currentHourNow = new Date().getHours();

        if (currentHourNow >= workEndHour) {
          // Après l'heure de fin → demander clôture
          result.push({
            id: `fin-proche-${chantier.id}`,
            type: 'attention',
            categorie: 'fin_chantier',
            titre: 'Chantier terminé aujourd\'hui — Clôturer ?',
            description: `${chantier.nom_chantier} — Date de fin effective : aujourd'hui (${format(dateFin, 'dd/MM/yyyy')})`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            valeur: 0,
            donnees: {
              jours_restants: 0,
              date_fin_effective: dateFinEffectiveStr,
              date_fin_estimee: chantier.date_fin_estimee,
            },
            actions: [
              { label: 'Terminé', variant: 'success', type: 'navigate', destination: `/projects/${chantier.id}?terminer=true` },
              { label: 'Retard', variant: 'warning', type: 'navigate', destination: `/financial?tab=retard`, params: { chantierId: chantier.id } },
            ],
          });
        } else {
          // Avant l'heure de fin → alerte "se termine aujourd'hui"
          result.push({
            id: `fin-proche-${chantier.id}`,
            type: 'attention',
            categorie: 'fin_chantier',
            titre: 'Chantier se termine aujourd\'hui',
            description: `${chantier.nom_chantier} — Clôture à préparer (fin prévue : ${format(dateFin, 'dd/MM/yyyy')})`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            valeur: 0,
            donnees: {
              jours_restants: 0,
              date_fin_effective: dateFinEffectiveStr,
              date_fin_estimee: chantier.date_fin_estimee,
            },
            actions: [
              { label: 'Voir rentabilité', variant: 'primary', type: 'navigate', destination: `/financial?tab=rentabilite&chantier=${chantier.id}` },
              { label: 'Clôturer maintenant', variant: 'success', type: 'navigate', destination: `/projects/${chantier.id}?terminer=true` },
            ],
          });
        }
      }
      // CAS B : J-1 — Bientôt terminé (24h avant)
      else if (joursRestants > 0 && joursRestants <= seuils.fin_chantier_proche_jours) {
        result.push({
          id: `fin-proche-${chantier.id}`,
          type: 'attention',
          categorie: 'fin_chantier',
          titre: `Chantier bientôt terminé — J-${joursRestants}`,
          description: `${chantier.nom_chantier} se termine dans ${joursRestants} jour${joursRestants > 1 ? 's' : ''} — clôture à préparer`,
          chantierId: chantier.id,
          chantierNom: chantier.nom_chantier,
          valeur: joursRestants,
          seuil: seuils.fin_chantier_proche_jours,
          donnees: {
            jours_restants: joursRestants,
            date_fin_effective: dateFinEffectiveStr,
            date_fin_estimee: chantier.date_fin_estimee,
          },
          actions: [
            { label: 'Voir rentabilité', variant: 'primary', type: 'navigate', destination: `/financial?tab=rentabilite&chantier=${chantier.id}` },
            { label: 'Clôturer maintenant', variant: 'success', type: 'navigate', destination: `/projects/${chantier.id}?terminer=true` },
          ],
        });
      }
    });

    // 4. Rotations à planifier cette semaine
    // Helper: obtenir la vraie date de fin (dernière date dans jours_details si présent)
    const getRealEndDate = (aff: AffectationData): string => {
      if (aff.jours_details && Array.isArray(aff.jours_details) && aff.jours_details.length > 0) {
        // Trier par date et prendre la dernière
        const sortedDates = [...aff.jours_details].sort((a, b) => a.date.localeCompare(b.date));
        return sortedDates[sortedDates.length - 1].date;
      }
      return aff.date_fin.split('T')[0];
    };

    const finDemain = addDays(today, 1);
    const finDemainStr = format(finDemain, 'yyyy-MM-dd');
    const rotationsAVenir = affectations.filter((a) => {
      const dateFinReelle = getRealEndDate(a);
      return dateFinReelle > todayStr && dateFinReelle <= finDemainStr;
    });

    if (rotationsAVenir.length > 0) {
      // Préparer la liste détaillée des rotations
      const rotationsDetails = rotationsAVenir.map((a) => {
        const dateFinReelle = getRealEndDate(a);
        // Pour les équipes, lister les membres
        let membreNom = '';
        if (a.membres_equipe) {
          membreNom = `${a.membres_equipe.prenom} ${a.membres_equipe.nom}`;
        } else if (a.equipe_id) {
          const membresEquipe = membres.filter(m => m.equipe_id === a.equipe_id);
          if (membresEquipe.length > 0) {
            membreNom = membresEquipe.map(m => `${m.prenom} ${m.nom}`).join(', ');
          } else {
            membreNom = a.equipes?.nom || 'Équipe';
          }
        }
        return {
          id: a.id,
          membre: membreNom,
          chantier: a.chantiers?.nom_chantier || 'Chantier',
          date_fin: dateFinReelle,
        };
      });

      result.push({
        id: 'rotations-semaine',
        type: 'attention',
        categorie: 'rotation',
        titre: 'Rotations à planifier',
        description: `${rotationsAVenir.length} affectation${rotationsAVenir.length > 1 ? 's' : ''} se termine${rotationsAVenir.length > 1 ? 'nt' : ''} cette semaine`,
        valeur: rotationsAVenir.length,
        donnees: {
          rotations: rotationsDetails,
        },
        actions: [
          { label: 'Voir le planning', variant: 'primary', type: 'function', params: { action: 'open_planning' } },
        ],
      });
    }

    // 5. Budget proche du seuil - SUPPRIMÉ (intégré dans la logique de déduplication budget section 3)

    // ══════════════════════════════════════════════════════════════════
    // 🟠 ATTENTION : Suspensions de chantiers
    // ══════════════════════════════════════════════════════════════════

    // Libellés des raisons de suspension
    const RAISONS_LABELS: Record<string, string> = {
      intemperies: 'Intempéries',
      attente_materiaux: 'Attente matériaux',
      probleme_client: 'Problème client',
      autre: 'Autre',
    };

    // 6. Suspensions actives depuis longtemps - seuil personnalisable
    suspensionsActives.forEach((suspension) => {
      const dateSuspension = parseISO(suspension.date_suspension);
      const joursSuspension = differenceInDays(today, dateSuspension);
      const chantierNom = suspension.chantiers?.nom_chantier || 'Chantier';
      const raisonLabel = RAISONS_LABELS[suspension.raison] || suspension.raison;

      // Vérifier si la date de reprise est dépassée (pour éviter doublon avec reprise-depassee)
      const hasRepriseDepassee = suspension.date_reprise_estimee
        ? isAfter(today, parseISO(suspension.date_reprise_estimee))
        : false;

      // Alerte si suspension > seuil ET pas de reprise dépassée (sinon reprise-depassee est plus actionnable)
      if (joursSuspension >= seuils.suspension_longue_jours && !hasRepriseDepassee) {
        result.push({
          id: `suspension-longue-${suspension.id}`,
          type: 'attention',
          categorie: 'suspension',
          titre: 'Chantier suspendu depuis longtemps',
          description: `${chantierNom} suspendu depuis ${joursSuspension} jours (${raisonLabel})`,
          chantierId: suspension.chantier_id,
          chantierNom,
          valeur: joursSuspension,
          donnees: {
            suspension_id: suspension.id,
            date_suspension: suspension.date_suspension,
            jours_suspension: joursSuspension,
            raison: suspension.raison,
            raison_label: raisonLabel,
            type_suspension: suspension.type_suspension,
          },
          actions: [
            { label: 'Reprendre', variant: 'success', type: 'navigate', destination: `/projects/${suspension.chantier_id}?reprise=true` },
            { label: 'Terminer', variant: 'secondary', type: 'navigate', destination: `/projects/${suspension.chantier_id}?terminer=true` },
            { label: 'Annuler', variant: 'danger', type: 'navigate', destination: `/projects/${suspension.chantier_id}?annuler=true` },
          ],
        });
      }

      // Alerte si date de reprise estimée dépassée
      if (suspension.date_reprise_estimee) {
        const dateRepriseEstimee = parseISO(suspension.date_reprise_estimee);
        const joursRestants = differenceInDays(dateRepriseEstimee, today);

        if (isAfter(today, dateRepriseEstimee)) {
          // Date dépassée
          const joursDepassement = differenceInDays(today, dateRepriseEstimee);
          result.push({
            id: `reprise-depassee-${suspension.id}`,
            type: 'attention',
            categorie: 'suspension',
            titre: 'Date de reprise dépassée',
            description: `La reprise de ${chantierNom} était prévue le ${format(dateRepriseEstimee, 'dd/MM/yyyy')} (+${joursDepassement}j)`,
            chantierId: suspension.chantier_id,
            chantierNom,
            valeur: joursDepassement,
            donnees: {
              suspension_id: suspension.id,
              date_reprise_estimee: suspension.date_reprise_estimee,
              jours_depassement: joursDepassement,
            },
            actions: [
              { label: 'Reprendre', variant: 'success', type: 'navigate', destination: `/projects/${suspension.chantier_id}?reprise=true` },
              { label: 'Reporter', variant: 'secondary', type: 'navigate', destination: `/projects/${suspension.chantier_id}?reporter=true` },
            ],
          });
        } else if (joursRestants > 0 && joursRestants <= 3) {
          // Reprise proche (J-1 à J-3)
          result.push({
            id: `reprise-proche-${suspension.id}`,
            type: 'attention',
            categorie: 'suspension',
            titre: `Reprise dans ${joursRestants} jour${joursRestants > 1 ? 's' : ''}`,
            description: `${chantierNom} - Reprise prévue le ${format(dateRepriseEstimee, 'dd/MM/yyyy')}. Pensez à préparer l'équipe.`,
            chantierId: suspension.chantier_id,
            chantierNom,
            valeur: joursRestants,
            donnees: {
              suspension_id: suspension.id,
              date_reprise_estimee: suspension.date_reprise_estimee,
              jours_restants: joursRestants,
            },
            actions: [
              { label: 'Reprendre maintenant', variant: 'success', type: 'navigate', destination: `/projects/${suspension.chantier_id}?reprise=true` },
              { label: 'Voir chantier', variant: 'secondary', type: 'navigate', destination: `/projects/${suspension.chantier_id}` },
            ],
          });
        }
      }
    });

    // ══════════════════════════════════════════════════════════════════
    // Déduplication suspensions : 1 seule alerte par chantier
    // Priorité : reprise-depassee > suspension-longue > reprise-proche
    // ══════════════════════════════════════════════════════════════════
    const suspensionPriorite: Record<string, number> = {
      'reprise-depassee': 0,
      'suspension-longue': 1,
      'reprise-proche': 2,
    };
    const bestSuspensionPerChantier = new Map<string, number>();
    result.forEach((alerte, index) => {
      if (alerte.categorie !== 'suspension' || !alerte.chantierId) return;
      const prefix = alerte.id.split('-').slice(0, 2).join('-');
      const prio = suspensionPriorite[prefix] ?? 99;
      const existing = bestSuspensionPerChantier.get(alerte.chantierId);
      if (existing === undefined) {
        bestSuspensionPerChantier.set(alerte.chantierId, index);
      } else {
        const existingPrefix = result[existing].id.split('-').slice(0, 2).join('-');
        const existingPrio = suspensionPriorite[existingPrefix] ?? 99;
        if (prio < existingPrio) {
          bestSuspensionPerChantier.set(alerte.chantierId, index);
        }
      }
    });
    const bestSuspensionIndices = new Set(bestSuspensionPerChantier.values());
    for (let i = result.length - 1; i >= 0; i--) {
      if (result[i].categorie !== 'suspension' || !result[i].chantierId) continue;
      if (!bestSuspensionIndices.has(i)) {
        result.splice(i, 1);
      }
    }

    // ══════════════════════════════════════════════════════════════════
    // 🔵 INFO (Bonnes nouvelles / Rappels)
    // ══════════════════════════════════════════════════════════════════

    // 1. Chantiers très rentables — SUPPRIMÉ (pas actionnable, info visible sur la card)

    // 2. Chantiers terminés à facturer (DÉSACTIVÉ - redondant avec facturation-manquante-${id})
    // L'alerte facturation-manquante par chantier est plus spécifique et actionnable
    // if (chantiersTermines.length > 0) {
    //   const nbAFacturer = chantiersTermines.length;
    //   if (nbAFacturer > 0) {
    //     const chantiersDetails = chantiersTermines.map((c) => ({
    //       id: c.id,
    //       nom: c.nom_chantier,
    //       montant: c.devis?.find((d) => d.actif)?.montant_ht || c.budget_ht || 0,
    //     }));
    //     const premierChantier = chantiersDetails[0];
    //     result.push({
    //       id: 'chantiers-a-facturer',
    //       type: 'info',
    //       categorie: 'facture',
    //       titre: 'Chantiers à facturer',
    //       description: `${nbAFacturer} chantier${nbAFacturer > 1 ? 's' : ''} terminé${nbAFacturer > 1 ? 's' : ''} — vérifier la facturation`,
    //       valeur: nbAFacturer,
    //       donnees: { chantiers: chantiersDetails },
    //       actions: [
    //         { label: 'Voir tous', variant: 'primary', type: 'navigate', destination: `/financial?tab=paiement&chantier=${premierChantier.id}` },
    //       ],
    //     });
    //   }
    // }

    // 3. Planning du jour — SUPPRIMÉ (redondant avec le PlanningPanel adjacent)

    // ══════════════════════════════════════════════════════════════════
    // 🟠 ATTENTION (suite) : Alertes actionnables
    // ══════════════════════════════════════════════════════════════════

    // 6. Chantier terminé aujourd'hui — FUSIONNÉ dans "Chantier bientôt terminé" (section 3 ci-dessus)

    // 7. Facturation manquante (chantier terminé sans devis accepté)
    // On vérifie si le chantier a un devis avec statut 'accepte'
    chantiersTermines.forEach((chantier) => {
      const devisAccepte = chantier.devis?.find((d) => d.statut === 'accepte');
      // Si pas de devis accepté, alerte facturation
      if (!devisAccepte) {
        const devisActif = chantier.devis?.find((d) => d.actif) || chantier.devis?.[0];
        const montantHT = devisActif?.montant_ht || chantier.budget_ht || 0;
        const dateFinReelle = chantier.date_fin_reelle
          ? format(parseISO(chantier.date_fin_reelle), 'dd/MM/yyyy')
          : 'récemment';

        result.push({
          id: `facturation-manquante-${chantier.id}`,
          type: 'attention',
          categorie: 'paiement',
          titre: `Facturation manquante : "${chantier.nom_chantier}"`,
          description: `Chantier terminé le ${dateFinReelle} — Devis : ${montantHT.toLocaleString('fr-FR')} € HT`,
          chantierId: chantier.id,
          chantierNom: chantier.nom_chantier,
          donnees: { montant_ht: montantHT, date_fin: chantier.date_fin_reelle },
          actions: [
            {
              label: 'Ajouter le paiement →',
              variant: 'primary',
              type: 'navigate',
              destination: `/financial?tab=paiement&chantier=${chantier.id}`,
            },
          ],
        });
      }
    });

    // 7b. Alertes post-clôture — Paiements client / factures ST incomplets
    chantiersTermines.forEach((chantier) => {
      if (!chantier.cloture_avec_succes || !chantier.date_fin_reelle) return;

      const dateFinReelleParsed = parseISO(chantier.date_fin_reelle);
      const joursSinceCloture = differenceInDays(today, dateFinReelleParsed);
      if (joursSinceCloture < 3 || joursSinceCloture > 30) return;

      const dateFormatee = format(dateFinReelleParsed, 'dd/MM/yyyy');
      const paiementData = paiementTotalsTermines[chantier.id];
      const contratsData = contratsSTTermines[chantier.id];
      const resteAEncaisser = paiementData?.reste_a_encaisser ?? 0;
      const resteSTFacturer = contratsData?.totalResteAFacturer ?? 0;

      // Alertes paiement client
      if (resteAEncaisser > 0) {
        const montantFormate = resteAEncaisser.toLocaleString('fr-FR');

        if (joursSinceCloture >= 20) {
          result.push({
            id: `paiement-post-cloture-j20-${chantier.id}`,
            type: 'critique',
            categorie: 'paiement',
            titre: `${joursSinceCloture}j après clôture : ${montantFormate} € impayé`,
            description: `"${chantier.nom_chantier}" clôturé le ${dateFormatee} — ${montantFormate} € HT toujours en attente`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            donnees: { reste_a_encaisser: resteAEncaisser, jours_post_cloture: joursSinceCloture },
            actions: [
              { label: 'Voir paiements', variant: 'primary', type: 'navigate', destination: `/financial?tab=paiement&chantier=${chantier.id}` },
            ],
          });
        } else if (joursSinceCloture >= 7) {
          result.push({
            id: `paiement-post-cloture-j7-${chantier.id}`,
            type: 'attention',
            categorie: 'paiement',
            titre: `${joursSinceCloture}j après clôture : ${montantFormate} € en attente`,
            description: `"${chantier.nom_chantier}" clôturé le ${dateFormatee} — ${montantFormate} € HT non encaissé`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            donnees: { reste_a_encaisser: resteAEncaisser, jours_post_cloture: joursSinceCloture },
            actions: [
              { label: 'Voir paiements', variant: 'primary', type: 'navigate', destination: `/financial?tab=paiement&chantier=${chantier.id}` },
            ],
          });
        } else {
          result.push({
            id: `paiement-post-cloture-j3-${chantier.id}`,
            type: 'info',
            categorie: 'paiement',
            titre: `Rappel : ${montantFormate} € à encaisser`,
            description: `"${chantier.nom_chantier}" clôturé le ${dateFormatee} — Pensez à encaisser ${montantFormate} € HT`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            donnees: { reste_a_encaisser: resteAEncaisser, jours_post_cloture: joursSinceCloture },
            actions: [
              { label: 'Voir paiements', variant: 'primary', type: 'navigate', destination: `/financial?tab=paiement&chantier=${chantier.id}` },
            ],
          });
        }
      }

      // Alertes contrats sous-traitant
      if (resteSTFacturer > 0) {
        const montantSTFormate = resteSTFacturer.toLocaleString('fr-FR');
        const nbContrats = contratsData!.nbContrats;

        if (joursSinceCloture >= 7) {
          result.push({
            id: `st-post-cloture-j7-${chantier.id}`,
            type: 'attention',
            categorie: 'paiement',
            titre: `Factures ST manquantes : ${montantSTFormate} €`,
            description: `"${chantier.nom_chantier}" clôturé le ${dateFormatee} — ${montantSTFormate} € HT de factures sous-traitant à recevoir (${nbContrats} contrat(s))`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            donnees: { reste_st_facturer: resteSTFacturer, jours_post_cloture: joursSinceCloture },
            actions: [
              { label: 'Voir le chantier', variant: 'primary', type: 'navigate', destination: `/projects/${chantier.id}?tab=expenses` },
            ],
          });
        } else {
          result.push({
            id: `st-post-cloture-j3-${chantier.id}`,
            type: 'info',
            categorie: 'paiement',
            titre: `Rappel : factures ST en attente (${montantSTFormate} €)`,
            description: `"${chantier.nom_chantier}" — ${montantSTFormate} € HT de factures sous-traitant à recevoir`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            donnees: { reste_st_facturer: resteSTFacturer, jours_post_cloture: joursSinceCloture },
            actions: [
              { label: 'Voir le chantier', variant: 'primary', type: 'navigate', destination: `/projects/${chantier.id}?tab=expenses` },
            ],
          });
        }
      }
    });

    // 8. Devis en attente - seuil personnalisable
    chantiers.forEach((chantier) => {
      const devisEnvoyes = chantier.devis?.filter((d) => d.statut === 'envoye') || [];
      devisEnvoyes.forEach((devis) => {
        const dateCreation = parseISO(devis.created_at);
        const joursAttente = differenceInDays(today, dateCreation);
        if (joursAttente >= seuils.devis_attente_jours) {
          result.push({
            id: `devis-attente-${devis.id}`,
            type: 'attention',
            categorie: 'devis',
            titre: `Devis en attente depuis ${joursAttente}j`,
            description: `"${chantier.nom_chantier}" — ${devis.montant_ht.toLocaleString('fr-FR')} € HT`,
            chantierId: chantier.id,
            chantierNom: chantier.nom_chantier,
            donnees: { devis_id: devis.id, montant_ht: devis.montant_ht, jours_attente: joursAttente },
            actions: [
              {
                label: 'Envoyer',
                variant: 'secondary',
                type: 'navigate',
                destination: `/financial?tab=quote&chantier=${chantier.id}&devis_action=envoye&devis_id=${devis.id}`,
              },
              {
                label: 'Accepté ✓',
                variant: 'success',
                type: 'navigate',
                destination: `/financial?tab=quote&chantier=${chantier.id}&devis_action=accepte&devis_id=${devis.id}`,
              },
              {
                label: 'Refusé ✗',
                variant: 'danger',
                type: 'navigate',
                destination: `/financial?tab=quote&chantier=${chantier.id}&devis_action=refuse&devis_id=${devis.id}`,
              },
            ],
          });
        }
      });
    });

    // ══════════════════════════════════════════════════════════════════
    // 🔵 SUGGESTION (Optimisations)
    // ══════════════════════════════════════════════════════════════════

    // 9. Analyse IA disponible (> 7 jours sans analyse ET cooldown inactif)
    // Cooldown = 6h entre deux analyses
    const COOLDOWN_HEURES = 6;
    const COOLDOWN_MINUTES = COOLDOWN_HEURES * 60;

    chantiers.forEach((chantier) => {
      if (chantier.etat_chantier !== 'en_cours') return;

      const analysesIA = chantier.analyses_ia || [];
      const derniereAnalyse = analysesIA.length > 0
        ? analysesIA.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
        : null;

      const joursDepuisAnalyse = derniereAnalyse
        ? differenceInDays(today, parseISO(derniereAnalyse.created_at))
        : 999;

      // Vérifier si le cooldown est actif (< 6h depuis la dernière analyse)
      const minutesDepuisAnalyse = derniereAnalyse
        ? (Date.now() - new Date(derniereAnalyse.created_at).getTime()) / (1000 * 60)
        : Infinity;
      const cooldownActif = minutesDepuisAnalyse < COOLDOWN_MINUTES;

      // Afficher la notification uniquement si :
      // - Aucune analyse effectuée (jamais fait)
      // - OU plus de 7 jours depuis la dernière analyse ET cooldown inactif (peut relancer)
      const peutAfficherNotif = !derniereAnalyse || (joursDepuisAnalyse >= 7 && !cooldownActif);

      if (peutAfficherNotif) {
        result.push({
          id: `analyse-ia-dispo-${chantier.id}`,
          type: 'suggestion',
          categorie: 'analyse_ia',
          titre: `Analyse IA disponible pour "${chantier.nom_chantier}"`,
          description: derniereAnalyse
            ? `Dernière analyse il y a ${joursDepuisAnalyse} jours`
            : 'Aucune analyse effectuée',
          chantierId: chantier.id,
          chantierNom: chantier.nom_chantier,
          donnees: { jours_depuis_analyse: joursDepuisAnalyse },
          actions: [
            {
              label: "Lancer l'analyse →",
              variant: 'primary',
              type: 'navigate',
              destination: `/projects/${chantier.id}?tab=ai-analysis&action=launch`,
            },
          ],
        });
      }
    });

    // 10. Rentabilité exceptionnelle — SUPPRIMÉ (pas actionnable, info visible sur la card)

    // ══════════════════════════════════════════════════════════════════
    // 🔴 ALERTES FACTURES CLIENTS - seuils personnalisables
    // ══════════════════════════════════════════════════════════════════
    facturesClients.forEach((facture) => {
      const echeance = parseISO(facture.date_echeance);
      const diffJours = differenceInDays(echeance, today);
      const chantierNom = facture.chantiers?.nom_chantier || 'Chantier';
      const montantFormate = facture.montant_ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      if (diffJours < -seuils.facture_retard_jours) {
        // 🔴 CRITIQUE - Facture en retard de paiement
        const joursRetard = Math.abs(diffJours);
        result.push({
          id: `facture-retard-${facture.id}`,
          type: 'critique',
          categorie: 'paiement',
          titre: 'Facture en retard de paiement',
          description: `${facture.numero_facture} — ${montantFormate} € TTC — Échéance dépassée de ${joursRetard} jour${joursRetard > 1 ? 's' : ''}`,
          chantierId: facture.chantier_id,
          chantierNom,
          valeur: joursRetard,
          seuil: seuils.facture_retard_jours,
          donnees: {
            facture_id: facture.id,
            numero_facture: facture.numero_facture,
            montant_ttc: facture.montant_ttc,
            date_echeance: facture.date_echeance,
            jours_retard: joursRetard,
          },
          actions: [
            {
              label: 'Voir le chantier',
              variant: 'secondary',
              type: 'navigate',
              destination: `/projects/${facture.chantier_id}?tab=paiement`,
            },
            {
              label: 'Relancer',
              variant: 'primary',
              type: 'navigate',
              destination: `/financial?tab=paiement&chantier=${facture.chantier_id}`,
            },
          ],
        });
      } else if (diffJours >= 0 && diffJours <= seuils.facture_rappel_jours) {
        // 🟠 ATTENTION - Facture bientôt à échéance
        result.push({
          id: `facture-echeance-${facture.id}`,
          type: 'attention',
          categorie: 'paiement',
          titre: 'Facture bientôt à échéance',
          description: `${facture.numero_facture} — ${montantFormate} € TTC — Échéance dans ${diffJours} jour${diffJours > 1 ? 's' : ''}`,
          chantierId: facture.chantier_id,
          chantierNom,
          valeur: diffJours,
          seuil: seuils.facture_rappel_jours,
          donnees: {
            facture_id: facture.id,
            numero_facture: facture.numero_facture,
            montant_ttc: facture.montant_ttc,
            date_echeance: facture.date_echeance,
            jours_avant_echeance: diffJours,
          },
          actions: [
            {
              label: 'Voir le chantier',
              variant: 'secondary',
              type: 'navigate',
              destination: `/projects/${facture.chantier_id}?tab=paiement`,
            },
          ],
        });
      }
    });

    // ══════════════════════════════════════════════════════════════════
    // 🔴 ALERTES CONFLITS ABSENCE/AFFECTATION
    // ══════════════════════════════════════════════════════════════════

    // Grouper les conflits par membre pour éviter les alertes en double
    const conflitsParMembre = new Map<string, ConflitAbsenceAffectation[]>();
    for (const conflit of conflitsAbsence) {
      const key = conflit.membre_id;
      if (!conflitsParMembre.has(key)) {
        conflitsParMembre.set(key, []);
      }
      conflitsParMembre.get(key)!.push(conflit);
    }

    // Créer une alerte par membre en conflit
    for (const [membreId, conflits] of conflitsParMembre) {
      const premier = conflits[0];
      const totalJours = conflits.reduce((sum, c) => sum + c.jours_conflit, 0);
      const chantiersImpactes = [...new Set(conflits.map((c) => c.chantier_nom))];

      result.push({
        id: `conflit-absence-${membreId}`,
        type: 'critique',
        categorie: 'conflits_absence',
        titre: 'Conflit absence/affectation',
        description: `${premier.membre_prenom} ${premier.membre_nom} est en ${getTypeAbsenceLabel(premier.type_absence)} mais affecté sur ${chantiersImpactes.length} chantier${chantiersImpactes.length > 1 ? 's' : ''} (${totalJours} jour${totalJours > 1 ? 's' : ''} impacté${totalJours > 1 ? 's' : ''})`,
        membreId: membreId,
        membreNom: `${premier.membre_prenom} ${premier.membre_nom}`,
        valeur: totalJours,
        donnees: {
          conflits: conflits,
          type_absence: premier.type_absence,
          absence_debut: premier.absence_debut,
          absence_fin: premier.absence_fin,
          chantiers_impactes: chantiersImpactes,
        },
        actions: [
          {
            label: 'Voir l\'équipe',
            variant: 'secondary',
            type: 'navigate',
            destination: '/team',
          },
          {
            label: 'Voir le planning',
            variant: 'primary',
            type: 'navigate',
            destination: '/dashboard?panel=planning',
          },
        ],
      });
    }

    // ══════════════════════════════════════════════════════════════════
    // Trier par priorité : critique > attention > info > suggestion
    // ══════════════════════════════════════════════════════════════════
    const priorite: Record<string, number> = { critique: 0, attention: 1, info: 2, suggestion: 3 };
    result.sort((a, b) => priorite[a.type] - priorite[b.type]);

    return result;
  }, [chantiers, chantiersTermines, affectations, membres, equipes, suspensionsActives, facturesClients, seuils, budgetComparisons, conflitsAbsence, datesFinEffectives, paiementTotalsTermines, contratsSTTermines]);

  // ══════════════════════════════════════════════════════════════════
  // Système d'alertes "lues" (masquées) via localStorage + Supabase
  // ══════════════════════════════════════════════════════════════════
  const STORAGE_KEY = 'b8ild_alertes_lues';

  // Charger les alertes lues depuis localStorage (cache local immédiat)
  const [alertesLues, setAlertesLues] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Nettoyer les entrées de plus de 7 jours (localStorage seulement)
        const now = Date.now();
        const valid = Object.entries(parsed)
          .filter(([, timestamp]) => now - (timestamp as number) < 7 * 24 * 60 * 60 * 1000)
          .map(([id]) => id);
        return new Set(valid);
      }
    } catch (e) {
      console.error('[useAlertes] Erreur lecture localStorage:', e);
    }
    return new Set();
  });

  // Combiner alertesLues (localStorage) et alertesLuesDB (Supabase)
  const alertesLuesCombinees = useMemo(() => {
    const combined = new Set(alertesLues);
    alertesLuesDB.forEach(key => combined.add(key));
    return combined;
  }, [alertesLues, alertesLuesDB]);

  // Marquer une alerte comme lue (localStorage + Supabase)
  const marquerCommeLue = useCallback(async (alerteId: string) => {
    // 1. Mise à jour immédiate localStorage (UX instantanée)
    setAlertesLues((prev) => {
      const newSet = new Set(prev);
      newSet.add(alerteId);
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        const parsed = stored ? JSON.parse(stored) : {};
        parsed[alerteId] = Date.now();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
      } catch (e) {
        console.error('[useAlertes] Erreur sauvegarde localStorage:', e);
      }
      return newSet;
    });

    // 2. Persister en Supabase (si user connecté)
    if (user?.id) {
      try {
        const { error } = await supabase
          .from('alertes_lues')
          .upsert(
            { user_id: user.id, alerte_key: alerteId },
            { onConflict: 'user_id,alerte_key' }
          );

        if (error) {
          console.error('[useAlertes] Erreur persist alertes_lues Supabase:', error);
        } else {
          // Mettre à jour le state DB
          setAlertesLuesDB(prev => {
            const newSet = new Set(prev);
            newSet.add(alerteId);
            return newSet;
          });
          // Invalider le cache React Query pour synchroniser
          if (entrepriseId) {
            invalidateAlertes(queryClient, entrepriseId);
          }
        }
      } catch (e) {
        console.error('[useAlertes] Erreur persist alertes_lues:', e);
      }
    }
  }, [user?.id, entrepriseId, queryClient]);

  // ══════════════════════════════════════════════════════════════════
  // 🧪 MODE TEST : Alertes fictives pour voir toutes les notifications
  // ══════════════════════════════════════════════════════════════════
  const MODE_TEST_ALERTES = false; // Mettre à true pour activer le mode test

  const alertesTest: Alerte[] = useMemo(() => {
    if (!MODE_TEST_ALERTES) return [];

    return [
      // ═══ CRITIQUES ═══
      {
        id: 'test-marge-negative',
        type: 'critique',
        categorie: 'budget',
        titre: 'Marge négative',
        description: 'Rénovation Villa Dupont passe en déficit : -2 450€ (-8.2%)',
        chantierId: 'test-1',
        chantierNom: 'Rénovation Villa Dupont',
        valeur: -8.2,
        donnees: { marge_pct: -8.2, marge_euros: -2450, budget_ht: 30000, cout_total: 32450 },
        actions: [
          { label: 'Voir le chantier', variant: 'secondary', type: 'navigate', destination: '/financial?tab=rentabilite&chantier=test-1' },
          { label: 'Analyser', variant: 'primary', type: 'navigate', destination: '/projects/test-1?tab=ai-analysis&action=launch' },
        ],
      },
      {
        id: 'test-retard-critique',
        type: 'critique',
        categorie: 'retard',
        titre: 'Chantier en retard',
        description: '"Extension Garage Martin" a dépassé sa date de fin de 5 jours (fin prévue : 30/12/2025)',
        chantierId: 'test-2',
        chantierNom: 'Extension Garage Martin',
        valeur: 5,
        donnees: { jours_retard: 5, date_fin_prevue: '2025-12-30', date_debut: '2025-11-15' },
        actions: [
          { label: 'Voir le chantier', variant: 'secondary', type: 'navigate', destination: '/projects/test-2' },
          { label: 'Terminer', variant: 'success', type: 'navigate', destination: '/projects/test-2?terminer=true' },
          { label: 'Retard', variant: 'danger', type: 'navigate', destination: '/financial?tab=penalty-simulator&chantier=test-2' },
        ],
      },
      {
        id: 'test-budget-depasse',
        type: 'critique',
        categorie: 'budget',
        titre: 'Budget dépassé',
        description: 'Terrasse Bois Leroy : dépenses dépassent le budget de +15%',
        chantierId: 'test-3',
        chantierNom: 'Terrasse Bois Leroy',
        valeur: 115,
        seuil: 100,
        donnees: { budget_consomme_pct: 115, depassement_pct: 15, budget_ht: 8000, cout_total: 9200 },
        actions: [
          { label: 'Voir les dépenses', variant: 'secondary', type: 'navigate', destination: '/financial?tab=factures&chantier=test-3' },
        ],
      },
      {
        id: 'test-sans-equipe',
        type: 'critique',
        categorie: 'equipe',
        titre: 'Chantier sans équipe',
        description: 'Cuisine Moderne Bernard démarre dans 2 jours mais aucune équipe affectée',
        chantierId: 'test-4',
        chantierNom: 'Cuisine Moderne Bernard',
        valeur: 2,
        donnees: { jours_avant_debut: 2, date_debut: '2026-01-06' },
        actions: [
          { label: 'Affecter une équipe', variant: 'primary', type: 'navigate', destination: '/projects/test-4?tab=team' },
        ],
      },

      // ═══ ATTENTION ═══
      {
        id: 'test-membres-sans-affectation',
        type: 'attention',
        categorie: 'equipe',
        titre: 'Membres sans affectation',
        description: 'Jean Dupont, Marie Martin, Pierre Durand',
        valeur: 3,
        donnees: {
          membres: [
            { id: 'm1', nom: 'Jean Dupont', specialite: 'Maçon' },
            { id: 'm2', nom: 'Marie Martin', specialite: 'Électricienne' },
            { id: 'm3', nom: 'Pierre Durand', specialite: 'Plombier' },
          ],
          nomsComplets: 'Jean Dupont, Marie Martin, Pierre Durand',
        },
        actions: [
          { label: 'Voir le planning', variant: 'primary', type: 'function', params: { action: 'open_planning' } },
        ],
      },
      {
        id: 'test-marge-faible',
        type: 'attention',
        categorie: 'budget',
        titre: 'Marge faible',
        description: 'Salle de bain Petit : marge à 12.5% (seuil min : 18%)',
        chantierId: 'test-5',
        chantierNom: 'Salle de bain Petit',
        valeur: 12.5,
        seuil: 18,
        donnees: { marge_pct: 12.5, marge_euros: 1875, seuil_min: 18 },
        actions: [
          { label: 'Voir coûts', variant: 'primary', type: 'navigate', destination: '/financial?tab=expenses&chantier=test-5' },
        ],
      },
      {
        id: 'test-fin-proche',
        type: 'attention',
        categorie: 'fin_chantier',
        titre: 'Chantier bientôt terminé',
        description: 'Piscine Lemoine se termine dans 3 jours — clôture à préparer',
        chantierId: 'test-6',
        chantierNom: 'Piscine Lemoine',
        valeur: 3,
        seuil: 7,
        donnees: { jours_restants: 3, date_fin_prevue: '2026-01-07' },
        actions: [
          { label: 'Voir rentabilité', variant: 'primary', type: 'navigate', destination: '/financial?tab=rentabilite&chantier=test-6' },
          { label: 'Clôturer maintenant', variant: 'success', type: 'navigate', destination: '/projects/test-6?terminer=true' },
        ],
      },
      {
        id: 'test-rotations-semaine',
        type: 'attention',
        categorie: 'rotation',
        titre: 'Rotations à planifier',
        description: '3 affectations se terminent cette semaine',
        valeur: 3,
        donnees: {
          rotations: [
            { id: 'r1', membre: 'Jean Dupont', chantier: 'Villa Dupont', date_fin: '2026-01-06' },
            { id: 'r2', membre: 'Marie Martin', chantier: 'Extension Martin', date_fin: '2026-01-08' },
            { id: 'r3', membre: 'Équipe Gros Œuvre', chantier: 'Terrasse Leroy', date_fin: '2026-01-10' },
          ],
        },
        actions: [
          { label: 'Voir le planning', variant: 'primary', type: 'function', params: { action: 'open_planning' } },
        ],
      },
      {
        id: 'test-budget-proche',
        type: 'attention',
        categorie: 'budget',
        titre: 'Budget proche du seuil',
        description: 'Aménagement Combles Roux : 87% du budget consommé',
        chantierId: 'test-7',
        chantierNom: 'Aménagement Combles Roux',
        valeur: 87,
        seuil: 80,
        donnees: { budget_consomme_pct: 87, budget_ht: 25000, cout_total: 21750 },
        actions: [
          { label: 'Voir détails', variant: 'primary', type: 'navigate', destination: '/financial?tab=rentabilite&chantier=test-7' },
        ],
      },
      {
        id: 'test-termine-aujourdhui',
        type: 'attention',
        categorie: 'fin_chantier',
        titre: 'Le chantier "Carport Blanc" est-il terminé ?',
        description: 'Date de fin prévue : aujourd\'hui (04/01/2026)',
        chantierId: 'test-8',
        chantierNom: 'Carport Blanc',
        donnees: { date_fin: new Date() },
        actions: [
          { label: 'Terminé', variant: 'success', type: 'navigate', destination: '/projects/test-8?terminer=true' },
          { label: 'Retard', variant: 'warning', type: 'navigate', destination: '/financial?tab=retard', params: { chantierId: 'test-8' } },
        ],
      },
      {
        id: 'test-facturation-manquante',
        type: 'attention',
        categorie: 'paiement',
        titre: 'Facturation manquante : "Clôture Jardin Moreau"',
        description: 'Chantier terminé le 28/12/2025 — Devis : 4 500 € HT',
        chantierId: 'test-9',
        chantierNom: 'Clôture Jardin Moreau',
        donnees: { montant_ht: 4500, date_fin: '2025-12-28' },
        actions: [
          { label: 'Ajouter le paiement →', variant: 'primary', type: 'navigate', destination: '/financial?tab=paiement&chantier=test-9' },
        ],
      },
      {
        id: 'test-devis-attente',
        type: 'attention',
        categorie: 'devis',
        titre: 'Devis en attente depuis 12j',
        description: '"Rénovation Façade Girard" — 18 500 € HT',
        chantierId: 'test-10',
        chantierNom: 'Rénovation Façade Girard',
        donnees: { devis_id: 'd1', montant_ht: 18500, jours_attente: 12 },
        actions: [
          { label: 'Envoyer', variant: 'secondary', type: 'navigate', destination: '/financial?tab=quote&chantier=test-10&devis_action=envoye&devis_id=d1' },
          { label: 'Accepté ✓', variant: 'success', type: 'navigate', destination: '/financial?tab=quote&chantier=test-10&devis_action=accepte&devis_id=d1' },
          { label: 'Refusé ✗', variant: 'danger', type: 'navigate', destination: '/financial?tab=quote&chantier=test-10&devis_action=refuse&devis_id=d1' },
        ],
      },

      // ═══ INFO ═══
      {
        id: 'test-rentable',
        type: 'info',
        categorie: 'performance',
        titre: 'Chantier rentable',
        description: 'Maison Neuve Boucher : marge finale 32.4%',
        chantierId: 'test-11',
        chantierNom: 'Maison Neuve Boucher',
        valeur: 32.4,
        donnees: { marge_pct: 32.4, marge_euros: 24300 },
        actions: [
          { label: 'Voir rentabilité', variant: 'primary', type: 'navigate', destination: '/financial?tab=rentabilite&chantier=test-11' },
        ],
      },
      {
        id: 'test-chantiers-a-facturer',
        type: 'info',
        categorie: 'facture',
        titre: 'Chantiers à facturer',
        description: '2 chantiers terminés — vérifier la facturation',
        valeur: 2,
        donnees: {
          chantiers: [
            { id: 'c1', nom: 'Agrandissement Petit', montant: 35000 },
            { id: 'c2', nom: 'Rénovation Cave Leroux', montant: 12500 },
          ],
        },
        actions: [
          { label: 'Voir tous', variant: 'primary', type: 'navigate', destination: '/projects' },
        ],
      },
      {
        id: 'test-planning-jour',
        type: 'info',
        categorie: 'planning',
        titre: 'Planning du jour',
        description: '5 membres sur 3 chantiers aujourd\'hui',
        valeur: 5,
        donnees: {
          planning: [
            { chantier: 'Villa Dupont', chantierId: 'c1', membres: ['Jean Dupont', 'Marie Martin'] },
            { chantier: 'Extension Martin', chantierId: 'c2', membres: ['Pierre Durand', 'Luc Bernard'] },
            { chantier: 'Terrasse Leroy', chantierId: 'c3', membres: ['Sophie Lefebvre'] },
          ],
          nb_membres: 5,
          nb_chantiers: 3,
        },
        actions: [
          { label: 'Planning', variant: 'primary', type: 'function', params: { action: 'open_planning' } },
        ],
      },

      // ═══ SUGGESTIONS ═══
      {
        id: 'test-analyse-ia-dispo',
        type: 'suggestion',
        categorie: 'analyse_ia',
        titre: 'Analyse IA disponible pour "Rénovation Totale Mercier"',
        description: 'Dernière analyse il y a 14 jours',
        chantierId: 'test-12',
        chantierNom: 'Rénovation Totale Mercier',
        donnees: { jours_depuis_analyse: 14 },
        actions: [
          { label: "Lancer l'analyse →", variant: 'primary', type: 'navigate', destination: '/projects/test-12?tab=ai-analysis&action=launch' },
        ],
      },
      {
        id: 'test-rentabilite-top',
        type: 'suggestion',
        categorie: 'performance',
        titre: '🏆 Excellente rentabilité : "Construction Hangar Ferrand"',
        description: 'Marge de 35.2% — Top 10% du secteur BTP !',
        chantierId: 'test-13',
        chantierNom: 'Construction Hangar Ferrand',
        donnees: { marge_pct: 35.2, marge_euros: 42240 },
        actions: [
          { label: 'Voir rentabilité', variant: 'primary', type: 'navigate', destination: '/financial?tab=rentabilite&chantier=test-13' },
        ],
      },
    ] as Alerte[];
  }, []);

  // Fusionner alertes réelles + alertes test
  const alertesAvecTest = useMemo(() => {
    if (MODE_TEST_ALERTES) {
      return [...alertesTest, ...alertes];
    }
    return alertes;
  }, [alertes, alertesTest]);

  // Filtrer les alertes non lues (combine localStorage + Supabase)
  const alertesNonLues = useMemo(() => {
    return alertesAvecTest.filter((a) => !alertesLuesCombinees.has(a.id));
  }, [alertesAvecTest, alertesLuesCombinees]);

  // Limiter à MAX_ALERTES_PAR_CHANTIER alertes par chantier (les plus prioritaires)
  const alertesLimitees = useMemo(() => {
    return limitAlertesParChantier(alertesNonLues);
  }, [alertesNonLues]);

  // Agréger les alertes similaires (si ≥2 alertes du même type+catégorie)
  const alertesAgregees = useMemo(() => {
    return aggregateAlertes(alertesLimitees);
  }, [alertesLimitees]);

  // Compteurs par type (après la limite — reflète ce qui est affiché)
  const compteurs = useMemo(() => ({
    critique: alertesLimitees.filter((a) => a.type === 'critique').length,
    attention: alertesLimitees.filter((a) => a.type === 'attention').length,
    info: alertesLimitees.filter((a) => a.type === 'info').length,
    suggestion: alertesLimitees.filter((a) => a.type === 'suggestion').length,
    total: alertesLimitees.length,
  }), [alertesLimitees]);

  return {
    alertes: alertesAgregees,
    compteurs,
    loading,
    refetch,
    marquerCommeLue,
    // Exposer les données pour les widgets
    chantiers,
    chantiersTermines,
    membres,
    affectations,
  };
}
